import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/api';

export interface LoadState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
  setData: (data: T | null) => void;
}

/** Carga con estado de error y recarga. `loader` debe venir de `useCallback` para no recargar en cada render. */
export function useLoad<T>(loader: () => Promise<T>): LoadState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const current = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await loader();
      if (current === seq.current) setData(result);
    } catch (err) {
      if (current === seq.current) setError(errorMessage(err));
    } finally {
      if (current === seq.current) setLoading(false);
    }
  }, [loader]);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { data, error, loading, reload, setData };
}

/** Mensaje para la persona: qr-api ya responde en español; la red caída tiene su propio texto. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.kind === 'forbidden') return err.message || 'Tu rol no tiene permiso para esta acción.';
    if (err.kind === 'rate_limited') return 'Hiciste muchas solicitudes seguidas. Espera un momento e intenta de nuevo.';
    return err.message;
  }
  return 'Algo falló. Intenta de nuevo.';
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
